export interface AgenticFeatureProvider {
  id: string;
  name: string;
  
  // Lifecycle methods
  onAgentIdChange?: (agentId: string) => void;
  onConversationClear?: () => void;
  onPromptSent?: (prompt: string) => void;
  onRunCompleted?: (messages: any[]) => void;
  onFocusChange?: (focusKey: string) => void;
  
  // Feature provider can suggest agent ID changes
  getPreferredAgentId?: () => string | null;
  
  // Feature provider can inject prompts
  getInitialPrompts?: () => string[];
  
  // Feature provider can return a prompt to auto-send when focus changes
  getPromptForFocus?: (focusKey: string) => string | null;
}

export interface AgenticSettings {
  agentId: string;
  applicationKey: string;
  assistantKey: string;
  featureKey?: string;
  focusKey?: string;
}

class AgenticFeatureRegistryClass {
  private providers: Map<string, AgenticFeatureProvider> = new Map();
  private settings: AgenticSettings = {
    agentId: '',
    applicationKey: '',
    assistantKey: ''
  };
  private settingsChangeListeners: Set<(settings: AgenticSettings) => void> = new Set();

  // Settings change subscription (for React components)
  subscribeToSettingsChanges(callback: (settings: AgenticSettings) => void): () => void {
    this.settingsChangeListeners.add(callback);
    // Return unsubscribe function
    return () => {
      this.settingsChangeListeners.delete(callback);
    };
  }

  private notifySettingsChanged(): void {
    const currentSettings = this.getSettings();
    this.settingsChangeListeners.forEach(listener => {
      listener(currentSettings);
    });
  }

  // Registry management
  register(provider: AgenticFeatureProvider): void {
    this.providers.set(provider.id, provider);
    console.log(`Registered AgenticFeatureProvider: ${provider.name}`);
  }

  unregister(providerId: string): void {
    this.providers.delete(providerId);
    console.log(`Unregistered AgenticFeatureProvider: ${providerId}`);
  }

  getProvider(providerId: string): AgenticFeatureProvider | undefined {
    return this.providers.get(providerId);
  }

  getAllProviders(): AgenticFeatureProvider[] {
    return Array.from(this.providers.values());
  }

  // Settings management
  updateSettings(newSettings: Partial<AgenticSettings>): void {
    console.log('🔍 [AgenticFeatureRegistry] Updating settings:', newSettings);
    const oldAgentId = this.settings.agentId;
    this.settings = { ...this.settings, ...newSettings };
    
    // Notify providers of agent ID change
    if (newSettings.agentId && newSettings.agentId !== oldAgentId) {
      console.log('🔍 [AgenticFeatureRegistry] Notifying agent ID change:', newSettings.agentId);
      this.notifyAgentIdChange(newSettings.agentId);
    }
    
    // Notify React components of settings change
    this.notifySettingsChanged();
  }

  getSettings(): AgenticSettings {
    return { ...this.settings };
  }

  /**
   * Set context for a feature-specific agent (clears applicationKey)
   * Use this for global features like analytics-dashboard
   */
  setFeatureContext(featureKey: string, agentId?: string, assistantKey?: string): void {
    const oldAgentId = this.settings.agentId;

    console.log('🔍 [AgenticFeatureRegistry] Setting feature context:', featureKey, agentId, assistantKey);
    
    this.settings = {
      ...this.settings, // Preserve existing settings like focusKey
      agentId: agentId || '',
      applicationKey: '', // Clear application context for global features
      assistantKey: assistantKey || this.settings.assistantKey || '',
      featureKey: featureKey
    };
    
    console.log(`🎯 Set feature context: ${featureKey} (cleared applicationKey, preserved focusKey: ${this.settings.focusKey})`);
    
    // Notify providers of agent ID change
    if (this.settings.agentId !== oldAgentId) {
      this.notifyAgentIdChange(this.settings.agentId);
    }
    
    // Notify React components of settings change
    this.notifySettingsChanged();
  }

  /**
   * Set context for an application-specific agent (clears featureKey)
   * Use this for application-bound features
   */
  setApplicationContext(applicationKey: string, assistantKey: string, agentId?: string): void {
    const oldAgentId = this.settings.agentId;
    
    this.settings = {
      ...this.settings, // Preserve existing settings like focusKey
      agentId: agentId || applicationKey,
      applicationKey: applicationKey,
      assistantKey: assistantKey,
      featureKey: undefined // Clear feature context for application-specific agents
    };
    
    console.log(`🎯 Set application context: ${applicationKey} (cleared featureKey, preserved focusKey: ${this.settings.focusKey})`);
    
    // Notify providers of agent ID change
    if (this.settings.agentId !== oldAgentId) {
      this.notifyAgentIdChange(this.settings.agentId);
    }
    
    // Notify React components of settings change
    this.notifySettingsChanged();
  }

  /**
   * Reset context to defaults (clears all settings including focusKey)
   */
  resetContext(): void {
    const oldAgentId = this.settings.agentId;
    
    this.settings = {
      agentId: '',
      applicationKey: '',
      assistantKey: '',
      featureKey: undefined,
      focusKey: undefined
    };
    
    console.log('🔄 Reset all context to defaults');
    
    // Notify providers of agent ID change if there was one
    if (oldAgentId) {
      this.notifyAgentIdChange('');
    }
    
    // Notify React components of settings change
    this.notifySettingsChanged();
  }

  // Lifecycle notifications
  notifyAgentIdChange(agentId: string): void {
    this.providers.forEach(provider => {
      provider.onAgentIdChange?.(agentId);
    });
  }

  notifyConversationClear(): void {
    this.providers.forEach(provider => {
      provider.onConversationClear?.();
    });
  }

  notifyPromptSent(prompt: string): void {
    this.providers.forEach(provider => {
      provider.onPromptSent?.(prompt);
    });
  }

  notifyRunCompleted(messages: any[]): void {
    this.providers.forEach(provider => {
      provider.onRunCompleted?.(messages);
    });
  }

  notifyFocusChange(focusKey: string): void {
    this.providers.forEach(provider => {
      provider.onFocusChange?.(focusKey);
    });
  }

  // Helper methods for feature providers
  getPreferredAgentId(): string | null {
    // Note: Removed verbose logging to prevent console spam during render
    for (const provider of this.providers.values()) {
      const preferredId = provider.getPreferredAgentId?.();
      if (preferredId) {
        return preferredId;
      }
    }
    return null;
  }

  getInitialPrompts(): string[] {
    const prompts: string[] = [];
    this.providers.forEach(provider => {
      const providerPrompts = provider.getInitialPrompts?.();
      if (providerPrompts) {
        prompts.push(...providerPrompts);
      }
    });
    return prompts;
  }

  /**
   * Get prompt for focus from registered providers
   * Returns the first non-null/non-empty prompt from providers
   */
  getPromptForFocus(focusKey: string): string | null {
    for (const provider of this.providers.values()) {
      const prompt = provider.getPromptForFocus?.(focusKey);
      if (prompt) {
        return prompt;
      }
    }
    return null;
  }

  // Public API for external systems
  sendPrompt(prompt: string): void {
    // This will be called by the ChatInterface when a prompt is sent
    this.notifyPromptSent(prompt);
  }

  clearConversation(): void {
    this.notifyConversationClear();
  }

  changeAgentId(agentId: string): void {
    console.log('🔍 [AgenticFeatureRegistry] Changing agent ID:', agentId);
    this.updateSettings({ agentId });
  }

  /**
   * Set focus key for the current context (e.g., dashboard ID, customer ID)
   * This will notify providers and can trigger auto-prompts in ChatInterface
   */
  setFocusKey(focusKey: string): void {
    const oldFocusKey = this.settings.focusKey;
    
    if (focusKey === oldFocusKey) {
      // No change, don't notify
      return;
    }
    
    this.settings.focusKey = focusKey;
    console.log(`🎯 Focus changed: ${focusKey}`);
    
    // Notify providers of focus change
    this.notifyFocusChange(focusKey);
    
    // Notify React components of settings change
    this.notifySettingsChanged();
  }

  /**
   * Clear the current focus key
   */
  clearFocusKey(): void {
    if (!this.settings.focusKey) {
      // Already cleared, don't notify
      return;
    }
    
    this.settings.focusKey = undefined;
    console.log('🔄 Focus cleared');
    
    // Notify React components of settings change
    this.notifySettingsChanged();
  }
}

// Global singleton instance
export const AgenticFeatureRegistry = new AgenticFeatureRegistryClass();

// Global window object for external access
declare global {
  interface Window {
    AgenticFeatureRegistry: typeof AgenticFeatureRegistry;
  }
}

// Make it globally available
if (typeof window !== 'undefined') {
  window.AgenticFeatureRegistry = AgenticFeatureRegistry;
} 